import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth, AppError } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { AuthShell } from '@/components/auth/AuthShell';
import { AuthHeading, EditorialField, MonoLabel, PillButton } from '@/components/editorial';

// The reset page's rule and messages, reused so one password rule reads the
// same everywhere (#2324). Built per render locale (#2109 L6).
const makeSchema = (t: TFunction<'auth'>) =>
  z
    .object({
      password: z
        .string()
        .min(8, t('reset.passwordMin'))
        .regex(/[A-Z]/, t('reset.passwordUppercase'))
        .regex(/[a-z]/, t('reset.passwordLowercase'))
        .regex(/[0-9]/, t('reset.passwordNumber')),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('reset.passwordsMismatch'),
      path: ['confirmPassword'],
    });

type FormData = z.infer<ReturnType<typeof makeSchema>>;

/**
 * /set-new-password (#2324). A sign-in path answered "password change
 * required": the person proved who they are, and must choose a new password
 * before any session exists. The single-use change token arrives as router
 * state; without it (a reload, a pasted URL) there is nothing to finish, so
 * the page returns to /login.
 */
export function SetNewPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { completeRequiredPasswordChange } = useAuth();
  const { t } = useTranslation('auth');
  const schema = useMemo(() => makeSchema(t), [t]);

  const state = location.state as { changeToken?: string; rememberMe?: boolean } | null;
  const changeToken = state?.changeToken;
  const rememberMe = state?.rememberMe ?? false;

  useEffect(() => {
    if (!changeToken) navigate('/login', { replace: true });
  }, [changeToken, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    if (!changeToken) return;
    try {
      await completeRequiredPasswordChange(changeToken, data.password, rememberMe);
    } catch (error: unknown) {
      const code = error instanceof AppError ? error.code : undefined;
      if (code === 'change_token_invalid') {
        toast({ variant: 'destructive', title: t('setNew.expiredTitle'), description: t('setNew.expiredDescription') });
        navigate('/login', { replace: true });
        return;
      }
      toast({
        variant: 'destructive',
        title: t('setNew.failedTitle'),
        description: code === 'password_unchanged' ? t('setNew.unchanged') : t('setNew.failedDescription'),
      });
    }
  };

  if (!changeToken) return null;

  return (
    <AuthShell
      documentLabel={t('setNew.documentLabel')}
      meta={[{ k: t('setNew.stepLabel'), v: t('setNew.stepValue') }]}
    >
      <header className="flex flex-col gap-4 mb-10">
        <MonoLabel>{t('setNew.eyebrow')}</MonoLabel>
        <AuthHeading>
          <Trans t={t} i18nKey="setNew.title" components={{ em: <em className="not-italic text-stevens-indigo" /> }} />
        </AuthHeading>
        <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
          {t('setNew.lede')}
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <EditorialField
          label={t('reset.newPasswordLabel')}
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.password?.message}
          helper={t('reset.newPasswordHelper')}
          {...register('password')}
        />
        <EditorialField
          label={t('reset.confirmPasswordLabel')}
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <div className="flex items-center gap-3 mt-2">
          <PillButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('setNew.saving')}
              </>
            ) : (
              <>{t('setNew.submit')} {"→"}</>
            )}
          </PillButton>
        </div>
      </form>
    </AuthShell>
  );
}
